import { cloneDeep } from 'lodash-es';
import { useToastMessageState } from '../../components/ToastMessage/ToastMessageProvider';
import { ICompanyDataModel } from '../../data-models/company.data-model';
import { fieldEqualsIgnoreUnset, partialEqualsIgnoreUnsetValues } from '../../util/partialEquals';
import { fetchCompanyRaw, updateCompany } from '../queries/MaggieCompanyQueries';
import { getErrorMessage } from '../queryHelpers';
import { getForesightStore } from '../../util/jotai-store';
import { peekCompanyMetrics, setCompanyMetricsAtomJ } from '../state/CompanyMetricsStateJ';
import { companyStateJ, getCompanySyncIfResolved } from '../state/CompanyStateJ';
import { trackEvent } from '../../util/datadogTracking';
import { useCallback } from 'react';

export function useUpdateCompanyAndState() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const store = getForesightStore();

  return useCallback(
    async (
      companyId: number,
      companyUpdates: Partial<ICompanyDataModel>,
      showSuccessMessage = true,
      optimisticallyUpdateCompanyMetrics = false,
      awaitBeforeUpdate = false
    ) => {
      const currentCompany = getCompanySyncIfResolved(companyId) ?? undefined;

      if (partialEqualsIgnoreUnsetValues(companyUpdates, currentCompany)) {
        return currentCompany;
      }

      let metricsRollback: ReturnType<typeof peekCompanyMetrics> | undefined;

      if (currentCompany && !awaitBeforeUpdate) {
        const updatedCompany = {
          ...cloneDeep(currentCompany),
          ...cloneDeep(companyUpdates),
        };

        store.set(companyStateJ(companyId), updatedCompany);

        if (optimisticallyUpdateCompanyMetrics) {
          const currentCompanyMetrics = peekCompanyMetrics({ companyId });
          if (currentCompanyMetrics) {
            metricsRollback = currentCompanyMetrics;
            store.set(setCompanyMetricsAtomJ, {
              params: { companyId },
              value: { ...currentCompanyMetrics, included: [cloneDeep(updatedCompany)] },
            });
          }
        }
      }

      try {
        const updatedCompany = await updateCompany({
          payload: prepareCompanyPayload(companyUpdates),
          companyId,
        });

        store.set(companyStateJ(companyId), updatedCompany);

        // Emit a single `company.field_updated` event listing every field that
        // actually changed. Full-form callers (e.g. the Formik company forms)
        // submit unedited fields too, so diff against the pre-update company,
        // tolerating unset normalization ('' vs null). No baseline, no events.
        // `EventAttrs` only allows primitive values, so the field list is
        // joined into a comma string.
        const changedFields = currentCompany
          ? Object.keys(companyUpdates).filter(
              (key) =>
                !fieldEqualsIgnoreUnset(
                  companyUpdates[key as keyof ICompanyDataModel],
                  currentCompany[key as keyof ICompanyDataModel]
                )
            )
          : [];
        if (changedFields.length > 0) {
          trackEvent('company.field_updated', { fields: changedFields.join(',') });
        }

        if (awaitBeforeUpdate && optimisticallyUpdateCompanyMetrics) {
          const currentCompanyMetrics = peekCompanyMetrics({ companyId });
          if (currentCompanyMetrics) {
            store.set(setCompanyMetricsAtomJ, {
              params: { companyId },
              value: { ...currentCompanyMetrics, included: [updatedCompany] },
            });
          }
        }

        if (showSuccessMessage) {
          pushSuccessToast({ message: 'Successfully updated company' });
        }
        return updatedCompany;
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to update company');
        // Roll back the optimistic Jotai write so the UI matches reality.
        if (currentCompany) {
          store.set(companyStateJ(companyId), currentCompany);
        }
        if (metricsRollback) {
          store.set(setCompanyMetricsAtomJ, {
            params: { companyId },
            value: metricsRollback,
          });
        }
        pushErrorToast({ message });
        return undefined;
      }
    },
    [pushErrorToast, pushSuccessToast, store]
  );
}

export function prepareCompanyPayload(data: Partial<ICompanyDataModel>) {
  const payload = cloneDeep(data);
  const internalDescription = data.shortDescription;
  delete payload.shortDescription;
  return { ...payload, internalDescription };
}

export function useUpdateCompanyStates() {
  const store = getForesightStore();
  return useCallback(
    async (companyId: number) => {
      try {
        const updatedCompany = await fetchCompanyRaw(companyId);
        store.set(companyStateJ(companyId), updatedCompany);
      } catch (err) {
        console.error(err);
      }
    },
    [store]
  );
}
